import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LabelInput } from '@/components/ui/inputs';
import { PrimaryButton } from '@/components/ui/buttons';
import { Text } from '@/components/ui/text';
import { Spinner } from '@/components/ui/loading';
import { IconScout } from '@/components/ui/images/IconScout';
import { colors } from '@/assets/styles/colors';
import type { SSOLinkAccountData } from '@/schemas/account';


interface SSOLinkAccountFormProps {
  register: UseFormRegister<SSOLinkAccountData>;
  handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  errors: FieldErrors<SSOLinkAccountData>;
  isSubmitting: boolean;
  isLoadingInfo: boolean;
  userNameTaken: boolean;
  onBackToHome: () => void;
}

export const SSOLinkAccountForm: React.FC<SSOLinkAccountFormProps> = ({
  register,
  handleSubmit,
  errors,
  isSubmitting,
  isLoadingInfo,
  userNameTaken,
  onBackToHome,
}) => {
  const { t: translate } = useTranslation();

  if (isLoadingInfo) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 pt-4">
      <LabelInput
        id="userName"
        label={translate('auth:ssoLinking.form.fields.userName')}
        color={errors.userName ? 'error' : 'important'}
        supportingText={
          userNameTaken
            ? translate('auth:ssoLinking.form.userNameTakenHint')
            : errors.userName?.message
        }
        required
        registration={register('userName')}
        disabled={!userNameTaken}
        iconRight={<IconScout name="user" size="md" />}
      />

      <LabelInput
        id="rut"
        label={translate('auth:ssoLinking.form.fields.rut')}
        color="important"
        registration={register('rut')}
        disabled
      />

      <LabelInput
        id="email"
        label={translate('auth:ssoLinking.form.fields.email')}
        color="important"
        registration={register('email')}
        disabled
      />

      <LabelInput
        id="password"
        type="password"
        label={translate('auth:ssoLinking.form.fields.password')}
        color={errors.password ? 'error' : 'important'}
        supportingText={errors.password?.message}
        required
        registration={register('password')}
      />

      <LabelInput
        id="confirmPassword"
        type="password"
        label={translate('auth:ssoLinking.form.fields.confirmPassword')}
        color={errors.confirmPassword ? 'error' : 'important'}
        supportingText={errors.confirmPassword?.message}
        required
        registration={register('confirmPassword')}
      />

      <div className="pt-2">
        <PrimaryButton
          type="submit"
          isLoading={isSubmitting}
          fullWidth
          iconRight={<IconScout name="sync" size="md" />}
        >
          {translate('auth:ssoLinking.form.submit')}
        </PrimaryButton>
      </div>

      <div className="text-center">
        <Text
          variant="body-medium"
          onClick={onBackToHome}
          className="cursor-pointer transition-colors hover:opacity-80"
          color={colors.primary.main}
        >
          {translate('auth:ssoLinking.form.backToHome')}
        </Text>
      </div>
      
      <div className="flex justify-center pt-2">
        <Text
          variant="subheader-regular"
          color={colors.grays.neutral66}
          className="text-center text-sm"
        >
          {translate('auth:ssoLinking.form.description')}
        </Text>
      </div>
    </form>
  );
};
